import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import {
  addPartnerProposalFiles,
  deletePartnerProposalFiles,
  type PPSignedAssetResponse,
  signDeveloperAsset,
} from '../../../../../../api/Rover/partnerProposal';
import { useFileUpload } from '../../../../../../hooks/useFileUpload';
import { notifyUser } from '../../../../../../store/Toast';
import type { SignDeveloperAssetPayload } from '../../../../../../types/Shared';
import { boardOfDirectorsMapping } from '../../../../../Applications/EnterpriseLBAForm/LeadershipInformation/constant';
import type { EnterpriseLBALeadershipInformationInterface, PartnerProposalFile } from '../../../../../Applications/EnterpriseLBAForm/type';
import AttachmentContainer, { Attachment } from '../Attachments';

interface LeadershipInformationDetailProps {
  partnerProposal: {
    formData: {
      leadershipInformation: EnterpriseLBALeadershipInformationInterface;
    };
    files: PartnerProposalFile[];
  };
}
export default function LeadershipInformationDetail({ partnerProposal: { formData, files } }: LeadershipInformationDetailProps) {
  const { upload } = useFileUpload({
    signAsset: signDeveloperAsset,
  });
  const leadershipFiles = files
    .map((file) => {
      if (file.field === 'principalsResume') {
        return file;
      }
      return undefined;
    })
    .filter(Boolean);
  const { leadershipInformation } = formData;
  const { id = 0 } = useParams();

  const dispatch = useDispatch();

  const queryClient = useQueryClient();

  const uploadFile = useMutation((payload: { signAssetPayload: SignDeveloperAssetPayload[]; files: File[] }) => upload(payload), {
    onSuccess: (response: any) => {
      uploadPartnerProposalFile.mutate(response);
    },
    onError: (_error: any) => {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Failed to upload document',
          toastType: 'failed',
        })
      );
    },
  });

  const uploadPartnerProposalFile = useMutation(
    (payload: { sign: PPSignedAssetResponse; files: File[]; response: any }) => {
      const { files, sign } = payload;
      return addPartnerProposalFiles({
        applicationId: id as number,
        files: [
          {
            name: files[0].name,
            path: sign.path,
            key: sign.key,
            type: files[0].type,
            field: 'principalsResume',
            description: 'Principals Resume',
          },
        ],
      });
    },
    {
      onSuccess: (_response: any) => {
        queryClient.invalidateQueries(['get-partner-proposal']);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Successfully Updated Developer Application',
            toastType: 'success',
          })
        );
      },
      onError: (_error: any) => {
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Failed to upload document',
            toastType: 'failed',
          })
        );
      },
    }
  );

  const handleUploadDocument = (files: File[]) => {
    if (leadershipInformation.principalsResume?.findIndex((resume) => resume.name === files[0].name) !== -1) {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'File already exists.',
          toastType: 'failed',
        })
      );
    } else {
      uploadFile.mutate({
        signAssetPayload: [
          {
            fileName: files[0].name,
            fileType: files[0].type,
            fileSize: files[0].size,
          },
        ],
        files,
      });
    }
  };

  const deleteFile = useMutation(
    (payload: { key: string }) =>
      deletePartnerProposalFiles({
        ...payload,
        applicationId: id as number,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['get-partner-proposal']);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Successfully deleted file',
            toastType: 'success',
          })
        );
      },
    }
  );

  const handleFileDelete = (key: string) => {
    deleteFile.mutate({ key });
  };

  return (
    <section aria-labelledby='applicant-information-title'>
      <div className='bg-white shadow sm:rounded-lg'>
        <div className='py-5'>
          <div className='px-4 py-2 sm:px-6 border-l-4 border-primaryColor-normal'>
            <h2 id='applicant-information-title' className='m-0 text-lg leading-6 font-medium text-primaryColor-normal'>
              Leadership Information
            </h2>
            <p className='mt-1 max-w-2xl text-sm text-gray-500'>Leadership details and application.</p>
          </div>
        </div>
        <div className='border-t border-gray-200 px-4 py-5 sm:px-6'>
          <dl className='grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2 mb-5'>
            <div className='sm:col-span-2'>
              <dt className='text-sm font-medium text-gray-500'>
                What racial/ethnic groups does the highest authority member of management (President, CEO, Partner, etc) belong to?
              </dt>
              <dd className='mt-1 text-sm text-gray-900'>
                {leadershipInformation.racialEthnicGroup.map((group: string) => (
                  <span key={group}>{group}, </span>
                ))}
              </dd>
            </div>
            <div className='sm:col-span-2'>
              <dt className='text-sm font-medium text-gray-500'>
                Is the majority ownership (51% or greater) of the company held by someone that is:
              </dt>
              <dd className='mt-1 text-sm text-gray-900'>
                {leadershipInformation.majorityOwnership.map((major: string) => (
                  <span key={major}>{major}, </span>
                ))}
              </dd>
            </div>
            <div className='sm:col-span-2'>
              <dt className='text-sm font-medium text-gray-500'>
                If the company is governed by a board of directors, please specify the percentage of the board that identifies with any of
                the groups below:
              </dt>
              <dd className='mt-1 text-sm text-gray-900'>
                {Object.keys(leadershipInformation.boardOfDirectorsPercent).map((key: string) => {
                  if (leadershipInformation.boardOfDirectorsPercent[key].trim() !== '') {
                    return (
                      <span key={key}>
                        {boardOfDirectorsMapping[key]}({leadershipInformation.boardOfDirectorsPercent[key]}
                        %),{' '}
                      </span>
                    );
                  }
                  return null;
                })}
              </dd>
            </div>
            {/* <div className="sm:col-span-2">
                <dt className="text-sm font-medium text-gray-500">
                  Resume of Principals
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  <ul
                    role="list"
                    className="border border-gray-200 rounded-md divide-y divide-gray-200"
                  >
                    {leadershipInformation.principalsResume.map(
                      (attachment: any) => (
                        <li
                          key={attachment.id}
                          className="pl-3 pr-4 py-3 flex items-center justify-between text-sm"
                        >
                          <div className="w-0 flex-1 flex items-center">
                            <PaperClipIcon
                              className="shrink-0 h-5 w-5 text-gray-400"
                              aria-hidden="true"
                            />
                            <span className="ml-2 flex-1 w-0 truncate">
                              {attachment.resume.name}
                            </span>
                          </div>
                          <div className="ml-4 shrink-0">
                            <a
                              href={attachment.href}
                              className="font-medium text-secondaryColor-medium hover:text-secondaryColor-normal cursor-pointer"
                            >
                              Download
                            </a>
                          </div>
                        </li>
                      )
                    )}
                  </ul>
                </dd>
              </div> */}
          </dl>
          <AttachmentContainer
            title='Principal Resume'
            uploadConfig={{
              onUpload: handleUploadDocument,
              isUploading: uploadFile.isLoading,
            }}
            isEmpty={!leadershipFiles || leadershipFiles?.length === 0}
          >
            {leadershipFiles?.map((doc) => (
              <Attachment
                key={doc?.url}
                title={doc?.name || ''}
                doc={doc}
                deleteFileHandler={handleFileDelete}
                onClick={() => {
                  window.open(doc?.url || '');
                }}
              />
            ))}
          </AttachmentContainer>
        </div>
      </div>
    </section>
  );
}
